import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MdClose, MdLock, MdLocationOn, MdEmail, MdPhone, MdBusiness, MdSave, MdVerified, MdPerson } from 'react-icons/md';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

export default function CompanyProfileModal({ onClose }) {
    const { user, setUser } = useAuth();
    const { t } = useLanguage();
    const [companyData, setCompanyData] = useState(null);
    const [form, setForm] = useState({
        username: user.username || '', oldPassword: '', newPassword: '', confirmPassword: ''
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const res = await api.get(`/companies/${user.companyId}`);
                setCompanyData(res.data.data);
            } catch (err) {
                toast.error(t('scada.fetchError'));
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [user.companyId, t]);

    const validate = () => {
        const errs = {};
        if (!form.username.trim()) errs.username = t('companyProfile.usernameRequired');
        if (!form.oldPassword) errs.oldPassword = t('companyProfile.currentPasswordRequired');
        if (form.newPassword || form.confirmPassword) {
            if (form.newPassword.length < 6) errs.newPassword = t('register.passwordMin');
            if (form.newPassword !== form.confirmPassword) errs.confirmPassword = t('register.passwordMismatch');
        }
        return errs;
    };

    const handleChange = (field) => (e) => {
        setForm(prev => ({ ...prev, [field]: e.target.value }));
        setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        if (Object.keys(errs).length > 0) { setErrors(errs); return; }

        try {
            setSaving(true);
            setErrors({});
            const res = await api.put('/companies/profile/update', {
                oldPassword: form.oldPassword,
                newPassword: form.newPassword || undefined,
                username: form.username.trim()
            });
            const updatedUsername = res.data?.data?.username;
            if (updatedUsername) {
                const updatedUser = { ...user, username: updatedUsername };
                setUser(updatedUser);
                localStorage.setItem('user', JSON.stringify(updatedUser));
            }
            toast.success(t('companyProfile.updated'));
            onClose();
        } catch (err) {
            const message = err.response?.data?.message || t('companyProfile.updateFailed');
            toast.error(message);

            if (err.response?.status === 400) {
                setForm(prev => ({ ...prev, oldPassword: '' }));
                setErrors({ oldPassword: message });
            }
        } finally {
            setSaving(false);
        }
    };

    const iconStyle = {
        position: 'absolute', left: 12, top: '50%',
        transform: 'translateY(-50%)', color: 'var(--text-muted)',
        fontSize: 17, zIndex: 1, pointerEvents: 'none'
    };

    return (
        <AnimatePresence>
            <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                onClick={(e) => e.target === e.currentTarget && onClose()}
            >
                <motion.div
                    className="modal"
                    initial={{ opacity: 0, scale: 0.9, y: 30 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                >
                    <div className="modal-header">
                        <div className="modal-title">
                            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(79,142,247,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                                <MdBusiness />
                            </div>
                            {t('companyProfile.title')}
                        </div>
                        <button className="modal-close" onClick={onClose}><MdClose /></button>
                    </div>

                    {loading ? (
                        <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner"></div></div>
                    ) : (
                        <form onSubmit={handleSubmit} autoComplete="off">
                            <div className="modal-body">

                                {/* Company Info Card */}
                                <div style={{
                                    background: 'linear-gradient(135deg, rgba(79,142,247,0.08) 0%, rgba(79,142,247,0.03) 100%)',
                                    border: '1px solid rgba(79,142,247,0.2)',
                                    borderRadius: 16,
                                    padding: '20px',
                                    marginBottom: 24,
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    {/* Decorative top bar */}
                                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, var(--primary), rgba(79,142,247,0.3))' }} />

                                    {/* Company Header */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, paddingTop: 4 }}>
                                        <div style={{
                                            width: 44, height: 44, borderRadius: 12,
                                            background: 'linear-gradient(135deg, rgba(79,142,247,0.3), rgba(79,142,247,0.1))',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 20, fontWeight: 800, color: 'var(--primary)',
                                            border: '1px solid rgba(79,142,247,0.3)'
                                        }}>
                                            {companyData?.name?.[0]?.toUpperCase()}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{companyData?.name}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
                                                <MdVerified style={{ fontSize: 12, color: 'var(--primary)' }} />
                                                <span style={{ fontSize: 11, color: 'var(--primary)' }}>{t('companyProfile.registeredCompany')}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Info Items */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                                            <MdLocationOn style={{ color: 'var(--primary)', opacity: 0.8, fontSize: 16, flexShrink: 0 }} />
                                            <div>
                                                <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{t('companyProfile.address')}</div>
                                                <div style={{ fontSize: 13, fontWeight: 500 }}>{companyData?.address || '—'}</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                                                <MdEmail style={{ color: 'var(--primary)', opacity: 0.8, fontSize: 15, flexShrink: 0 }} />
                                                <div>
                                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{t('companyProfile.email')}</div>
                                                    <div style={{ fontSize: 12, fontWeight: 500 }}>{companyData?.email || '—'}</div>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                                                <MdPhone style={{ color: 'var(--primary)', opacity: 0.8, fontSize: 15, flexShrink: 0 }} />
                                                <div>
                                                    <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{t('companyProfile.phone')}</div>
                                                    <div style={{ fontSize: 12, fontWeight: 500 }}>{companyData?.telNo || '—'}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                                    <MdPerson style={{ color: 'var(--primary)' }} />
                                    <h4 style={{ margin: 0, fontSize: 14 }}>{t('companyProfile.loginInfo')}</h4>
                                </div>

                                {/* Giriş Adı */}
                                <div className="form-group">
                                    <label className="form-label">{t('companyProfile.username')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={iconStyle}><MdPerson /></span>
                                        <input
                                            className="form-input"
                                            type="text"
                                            placeholder={t('companyProfile.username')}
                                            value={form.username}
                                            onChange={handleChange('username')}
                                            style={{ paddingLeft: 38 }}
                                            disabled={saving}
                                            autoComplete="username"
                                        />
                                    </div>
                                    {errors.username && <div className="form-error">{errors.username}</div>}
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '20px 0 16px' }}>
                                    <MdLock style={{ color: 'var(--primary)' }} />
                                    <h4 style={{ margin: 0, fontSize: 14 }}>{t('companyProfile.newPassword')}</h4>
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: -12, marginBottom: 16 }}>
                                    {t('companyProfile.passwordOptionalNotice')}
                                </div>

                                {/* Mevcut Şifre */}
                                <div className="form-group">
                                    <label className="form-label">{t('companyProfile.currentPassword')}</label>
                                    <div style={{ position: 'relative' }}>
                                        <span style={iconStyle}><MdLock /></span>
                                        <input
                                            className="form-input"
                                            type="password"
                                            placeholder={t('companyProfile.currentPassword')}
                                            value={form.oldPassword}
                                            onChange={handleChange('oldPassword')}
                                            style={{ paddingLeft: 38 }}
                                            disabled={saving}
                                            autoComplete="current-password"
                                        />
                                    </div>
                                    {errors.oldPassword && <div className="form-error">{errors.oldPassword}</div>}
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                                    {/* Yeni Şifre */}
                                    <div className="form-group">
                                        <label className="form-label">{t('companyProfile.newPassword')}</label>
                                        <div style={{ position: 'relative' }}>
                                            <span style={iconStyle}><MdLock /></span>
                                            <input
                                                className="form-input"
                                                type="password"
                                                placeholder={t('companyProfile.newPassword')}
                                                value={form.newPassword}
                                                onChange={handleChange('newPassword')}
                                                style={{ paddingLeft: 38 }}
                                                disabled={saving}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                        {errors.newPassword && <div className="form-error">{errors.newPassword}</div>}
                                    </div>

                                    {/* Şifre Tekrar */}
                                    <div className="form-group">
                                        <label className="form-label">{t('companyProfile.confirmPassword')}</label>
                                        <div style={{ position: 'relative' }}>
                                            <span style={iconStyle}><MdLock /></span>
                                            <input
                                                className="form-input"
                                                type="password"
                                                placeholder={t('companyProfile.confirmPassword')}
                                                value={form.confirmPassword}
                                                onChange={handleChange('confirmPassword')}
                                                style={{ paddingLeft: 38 }}
                                                disabled={saving}
                                                autoComplete="new-password"
                                            />
                                        </div>
                                        {errors.confirmPassword && <div className="form-error">{errors.confirmPassword}</div>}
                                    </div>
                                </div>

                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                                    {t('common.close')}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? <><span className="spinner spinner-sm" /> {t('companyProfile.updating')}</> : <><MdSave /> {t('companyProfile.updateProfile')}</>}
                                </button>
                            </div>
                        </form>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
